import {
    AtSign,
    Blocks,
    BookOpen,
    BookUser,
    Bot,
    Box,
    Brain,
    CalendarCheck,
    CalendarClock,
    CalendarSync,
    ClipboardList,
    FileText,
    HardDrive,
    KeyRound,
    Layers,
    Link,
    Link2,
    ListChecks,
    ListFilter,
    Mail,
    MessagesSquare,
    Plug,
    MessageSquareText,
    Phone,
    PhoneCall,
    Send,
    Sparkles,
    TextCursorInput,
    UsersRound,
    Webhook,
    Workflow,
    Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { WhatsAppGlyph } from '@/components/shared/whatsapp-glyph';
import { TagPlusIcon } from '@/components/ui/tag';

import { humaniseKey } from '../utils/humanise-key';

/**
 * Display grouping for allocation views. Purely presentational — the SA
 * catalog has no categories, so the grouping lives here and an unknown key
 * lands in `other` rather than disappearing.
 */
export type ResourceGroup =
    | 'messaging'
    | 'audience'
    | 'automation'
    | 'ai'
    | 'scheduling'
    | 'team'
    | 'integrations'
    | 'storage'
    | 'other';

export interface IResourceGroupMeta {
    readonly key: ResourceGroup;
    readonly icon: LucideIcon;
    /** Default (English) label — pass through i18n at the call site. */
    readonly label: string;
}

/** Display order for grouped allocation views. */
export const RESOURCE_GROUPS: readonly IResourceGroupMeta[] = [
    { key: 'messaging', icon: MessagesSquare, label: 'Messaging' },
    { key: 'audience', icon: BookUser, label: 'Audience' },
    { key: 'automation', icon: Workflow, label: 'Automation' },
    { key: 'ai', icon: Brain, label: 'AI' },
    { key: 'scheduling', icon: CalendarClock, label: 'Scheduling' },
    { key: 'team', icon: UsersRound, label: 'Team' },
    { key: 'integrations', icon: Plug, label: 'Integrations' },
    { key: 'storage', icon: HardDrive, label: 'Storage' },
    { key: 'other', icon: Box, label: 'Other' },
];

export interface IResourceMeta {
    readonly key: string;
    readonly icon: LucideIcon;
    /** Default (English) display label — pass through i18n at the call site. */
    readonly label: string;
    /** Short unit noun for sublines (e.g. "emails", "credits"). */
    readonly unit: string;
    readonly group: ResourceGroup;
}

/**
 * Display metadata for the canonical resource catalog
 * (06-resource-catalog.md). The catalog is configured in the super-admin, so
 * the UI must render whatever keys arrive — unknown keys fall back to a
 * generic icon + humanised label and appear automatically.
 */
const REGISTRY: Record<string, Omit<IResourceMeta, 'key'>> = {
    contacts: { icon: BookUser, label: 'Contacts', unit: 'contacts', group: 'audience' },
    email_sends: { icon: Mail, label: 'Email', unit: 'emails', group: 'messaging' },
    sms_sends: { icon: MessageSquareText, label: 'SMS', unit: 'messages', group: 'messaging' },
    voice_minutes: { icon: PhoneCall, label: 'Voice minutes', unit: 'minutes', group: 'messaging' },
    ai_credits: { icon: Sparkles, label: 'AI credits', unit: 'credits', group: 'ai' },
    // Wallet credit-pool keys (Balancer §11) — distinct from the metered
    // sms_sends/email_sends resources above.
    sms_credits: {
        icon: MessageSquareText,
        label: 'SMS credits',
        unit: 'credits',
        group: 'messaging',
    },
    email_credits: { icon: Mail, label: 'Email credits', unit: 'credits', group: 'messaging' },
    active_users: { icon: UsersRound, label: 'Team seats', unit: 'seats', group: 'team' },
    static_segments: {
        icon: Layers,
        label: 'Static segments',
        unit: 'segments',
        group: 'audience',
    },
    dynamic_segments: {
        icon: ListFilter,
        label: 'Dynamic segments',
        unit: 'segments',
        group: 'audience',
    },
    active_sequences: {
        icon: Workflow,
        label: 'Sequences',
        unit: 'sequences',
        group: 'automation',
    },
    tags: { icon: TagPlusIcon, label: 'Tags', unit: 'tags', group: 'audience' },
    custom_fields: {
        icon: TextCursorInput,
        label: 'Custom fields',
        unit: 'fields',
        group: 'audience',
    },
    custom_modules: {
        icon: Blocks,
        label: 'Custom modules',
        unit: 'modules',
        group: 'integrations',
    },
    storage_mb: { icon: HardDrive, label: 'Storage (MB)', unit: 'MB', group: 'storage' },
    active_workflows: {
        icon: Workflow,
        label: 'Active workflows',
        unit: 'workflows',
        group: 'automation',
    },
    // Named for the module it governs. Only PUBLISHED forms occupy a slot —
    // drafts are free — which the label no longer says, so the unit carries it.
    lead_forms: {
        icon: ClipboardList,
        label: 'Lead forms',
        unit: 'published forms',
        group: 'audience',
    },
    meeting_types: {
        icon: CalendarClock,
        label: 'Meeting types',
        unit: 'meeting types',
        group: 'scheduling',
    },
    bookings: { icon: CalendarCheck, label: 'Bookings', unit: 'bookings', group: 'scheduling' },
    calendar_connections: {
        icon: CalendarSync,
        label: 'Calendar connections',
        unit: 'calendars',
        group: 'scheduling',
    },
    phone_numbers: { icon: Phone, label: 'Phone numbers', unit: 'numbers', group: 'messaging' },
    email_senders: { icon: AtSign, label: 'Email senders', unit: 'senders', group: 'messaging' },
    campaigns: { icon: Send, label: 'Campaigns', unit: 'campaigns', group: 'messaging' },
    polls: { icon: ListChecks, label: 'Polls', unit: 'polls', group: 'audience' },
    // Key stays `join_links` (persisted, drives enforcement); the label names
    // the module it actually governs — the web forms at /links.
    join_links: { icon: Link2, label: 'Web forms', unit: 'forms', group: 'audience' },
    join_keywords: {
        icon: MessageSquareText,
        label: 'Join keywords',
        unit: 'keywords',
        group: 'audience',
    },
    short_links: { icon: Link, label: 'Short links', unit: 'links', group: 'audience' },
    quick_messages: { icon: Zap, label: 'Quick messages', unit: 'messages', group: 'messaging' },
    sms_templates: {
        icon: FileText,
        label: 'SMS templates',
        unit: 'templates',
        group: 'messaging',
    },
    whatsapp_templates: {
        icon: WhatsAppGlyph,
        label: 'WhatsApp templates',
        unit: 'templates',
        group: 'messaging',
    },
    email_templates: {
        icon: Mail,
        label: 'Email templates',
        unit: 'templates',
        group: 'messaging',
    },
    webhook_endpoints: {
        icon: Webhook,
        label: 'Webhook endpoints',
        unit: 'endpoints',
        group: 'integrations',
    },
    api_keys: { icon: KeyRound, label: 'API keys', unit: 'keys', group: 'integrations' },
    knowledge_bases: {
        icon: BookOpen,
        label: 'Knowledge bases',
        unit: 'knowledge bases',
        group: 'ai',
    },
    // Product name is "AI employees"; the catalog key is ai_agents.
    ai_agents: { icon: Bot, label: 'AI employees', unit: 'employees', group: 'ai' },
    // Keys the SA catalog also emits (balancer bootstrap) — without an entry
    // they rendered as a second, humanised "Ai Employees" row.
    ai_employees: { icon: Bot, label: 'AI employees', unit: 'employees', group: 'ai' },
    whatsapp_sends: {
        icon: WhatsAppGlyph,
        label: 'WhatsApp sends',
        unit: 'messages',
        group: 'messaging',
    },
    workflows: { icon: Workflow, label: 'Workflows', unit: 'workflows', group: 'automation' },
};

export function getResourceMeta(key: string): IResourceMeta {
    const meta = REGISTRY[key];
    if (meta) return { key, ...meta };
    return { key, icon: Box, label: humaniseKey(key), unit: 'units', group: 'other' };
}

export function getResourceGroupMeta(group: ResourceGroup): IResourceGroupMeta {
    return (
        RESOURCE_GROUPS.find((item) => item.key === group) ??
        RESOURCE_GROUPS[RESOURCE_GROUPS.length - 1]!
    );
}
